
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, AlertCircle } from "lucide-react";
import { useState, useEffect } from "react";
import { toast } from "@/components/ui/use-toast";

interface LiveAuctionStreamProps {
  streamUrl: string;
  title: string;
}

const LiveAuctionStream = ({ streamUrl, title }: LiveAuctionStreamProps) => {
  const [loading, setLoading] = useState(true);
  const [connectionError, setConnectionError] = useState(false);

  useEffect(() => {
    // Reset states when stream URL changes
    setLoading(true);
    setConnectionError(false);
    
    // Simulate attempting to connect to the stream
    const timer = setTimeout(() => {
      // For demo purposes, simulate a successful connection for demo URLs
      // In a real implementation, this would be actual HLS player connection logic
      if (streamUrl.includes('hls.highwaymenauctions.com') || streamUrl.includes('example.com')) {
        setLoading(false);
        setConnectionError(false);
        toast({
          title: "Stream connected",
          description: "You are now viewing the live auction stream",
        });
      } else {
        setLoading(false);
        setConnectionError(true);
        toast({
          title: "Stream connection failed",
          description: "Unable to connect to the provided stream URL. Please verify the URL is correct.",
          variant: "destructive",
        });
      }
    }, 3000); // Longer timeout to simulate real connection attempt
    
    return () => clearTimeout(timer);
  }, [streamUrl]);

  return (
    <Card className="w-full overflow-hidden border-2 border-red-500 dark:border-red-700 shadow-lg">
      <div className="bg-red-500 dark:bg-red-700 text-white px-3 py-1 flex items-center justify-between">
        <h3 className="font-medium text-sm flex items-center">
          <span className="h-2 w-2 rounded-full bg-white mr-2 animate-pulse"></span>
          LIVE NOW
        </h3>
        <Badge variant="outline" className="text-white border-white">
          {title}
        </Badge>
      </div>
      <CardContent className="p-0">
        <AspectRatio ratio={16 / 9}>
          {loading ? (
            <div className="flex h-full w-full items-center justify-center bg-gray-100 dark:bg-gray-800">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2">Connecting to live stream...</span>
            </div>
          ) : connectionError ? (
            <div className="flex h-full w-full flex-col items-center justify-center bg-gray-100 dark:bg-gray-800 p-6">
              <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
              <h3 className="text-lg font-medium text-red-500 mb-2">Connection Failed</h3>
              <p className="text-center text-sm text-gray-600 dark:text-gray-400 mb-4">
                Unable to connect to the stream. Please check the stream URL and try again.
              </p>
              <div className="bg-gray-200 dark:bg-gray-700 p-4 rounded w-full max-w-md">
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">Stream URL:</p>
                <p className="text-xs font-mono text-gray-500 dark:text-gray-400 break-all">{streamUrl}</p>
              </div>
            </div>
          ) : (
            <div className="relative h-full w-full">
              <div className="absolute inset-0 bg-gray-800 flex items-center justify-center">
                <div className="text-center p-6">
                  <p className="text-white font-medium mb-4">Live Auction Stream</p>
                  <div className="bg-gray-700 p-4 rounded">
                    <p className="text-sm text-gray-300 mb-2">Connected to:</p>
                    <p className="text-xs font-mono text-gray-400 break-all">{streamUrl}</p>
                  </div>
                </div>
              </div>
              <div className="absolute top-4 right-4">
                <div className="flex items-center bg-red-600 text-white text-xs font-medium px-2 py-1 rounded">
                  <span className="h-2 w-2 rounded-full bg-white mr-1 animate-pulse"></span> LIVE
                </div>
              </div>
            </div>
          )}
        </AspectRatio>
      </CardContent>
    </Card>
  );
};

export default LiveAuctionStream;
